import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { ItemReviewAccessStore } from '@modules/common/components/item-review/item-review-access.store';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { ItemReviewDialogService } from '@modules/common/components/item-review/item-review-dialog.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { TaskHandoverDialogService } from '@modules/tasks/services/task-handover-dialog.service';
import { TasksService } from '@modules/tasks/services/tasks.service';
import { type Task } from '@modules/tasks/types/task';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { StitchType } from '@sharedlib/common/types/StitchType';

@Component({
  selector: 'app-subtask-actions',
  standalone: true,
  templateUrl: './subtask-actions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubtaskActionsComponent {
  public readonly subtask = input.required<Task>();
  private readonly space = inject(SpaceContextStore);
  private readonly accountKind = inject(AccountKindService);
  private readonly injector = inject(Injector);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly handoverDialog = inject(TaskHandoverDialogService);
  private readonly reviewDialog = inject(ItemReviewDialogService);
  private readonly reviewAccess = inject(ItemReviewAccessStore);
  private readonly tasks = inject(TasksService);
  protected readonly opened = signal(false);
  protected readonly enabled = computed(() => {
    const task = this.subtask();
    return (
      this.accountKind.isTeam() &&
      !!task.id &&
      this.space.type() === SpaceType.SHARED_SPACE &&
      task.accountId === this.space.contextId()
    );
  });
  protected readonly reviewDisabled = computed(
    () => this.reviewAccess.canRequest(this.subtask().id, this.subtask().accountId, StitchType.TASK) === false,
  );

  protected toggle(): void {
    this.opened.update((opened) => !opened);
  }

  protected assign(): void {
    this.opened.set(false);
    if (this.enabled()) this.handoverDialog.open(this.subtask(), this.injector);
  }

  protected requestReview(): void {
    this.opened.set(false);
    if (!this.enabled() || this.reviewDisabled()) return;
    const task = this.subtask();
    this.reviewDialog.open(
      { itemId: task.id, tenantId: task.accountId, stitchType: StitchType.TASK, api: this.tasks.reviewApi },
      this.injector,
    );
  }

  /**
   * The row menu's own Trash call (a PATCH under the subtask's owner), so anyone who can edit the subtask can remove it
   * without the permanent bin (#1610). On success every task list re-reads — the parent's subtask list (its count) too.
   */
  protected moveToTrash(): void {
    this.opened.set(false);
    if (!this.enabled()) return;
    const task = this.subtask();
    this.tasks
      .delete({ ids: [task.id] }, true, undefined, { requestContext: owningTenantContext(task.accountId) })
      .subscribe({ next: () => this.tasks.forceRefresh(), error: () => undefined });
  }

  @HostListener('keydown.escape', ['$event'])
  protected escape(event: Event): void {
    if (!this.opened()) return;
    event.preventDefault();
    event.stopPropagation();
    this.opened.set(false);
    this.element.nativeElement.querySelector<HTMLButtonElement>('button[aria-haspopup="menu"]')?.focus();
  }

  @HostListener('document:click', ['$event'])
  protected outsideClick(event: MouseEvent): void {
    if (event.target instanceof Node && !this.element.nativeElement.contains(event.target)) this.opened.set(false);
  }
}
